'use client';

import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, ExternalLink } from 'lucide-react';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { Announcement } from '@/types/announcemants';
import { formatDateStringWithDelimiter } from '@/utils/formatters/dateFormatter';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';
import ImageZoom from '@/components/modal/ImageZoom';

export interface AnnouncementListUnitProps {
    data: Announcement;
}

export default function AnnouncementListUnit({ data }: AnnouncementListUnitProps) {
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const { safePush } = useSafeNavigation();

    const handleClick = () => {
        if (data.url) {
            const isExternal = /^https?:\/\//i.test(data.url);
            if (Boolean(data.is_external_window)) {
                window.open(data.url, '_blank', 'noopener,noreferrer');
            } else if (isExternal) {
                window.location.href = data.url;
            } else {
                safePush(data.url);
            }
        } else {
            setIsZoomOpen(true);
        }
    };

    return (
        <>
            <div
                className="relative flex w-full pb-2 rounded-none md:rounded-xl bg-color-white-base md:shadow-full cursor-pointer"
                onClick={handleClick}
            >
                <motion.div className="flex flex-col w-full relative" whileHover="hovered" initial="initial">
                    <div className="relative w-full aspect-[4/3] px-2 pt-2">
                        <motion.img
                            src={data.eyecatch_public_url}
                            variants={{
                                initial: { scale: 1 },
                                hovered: { scale: 1.01 },
                            }}
                            transition={{ type: 'spring', stiffness: 300 }}
                            className="w-full h-full rounded-lg"
                        />
                        <div className="absolute top-4 right-4 bg-color-base font-cantarell font-bold text-xs px-2 py-1 rounded-3xl flex items-center gap-1 uppercase leading-none">
                            <span className="text-color-white-base">{data.hub_type_name_en}</span>
                            {data.url && Boolean(data.is_external_window) && (
                                <IconAdaptive
                                    Icon={ExternalLink}
                                    strokeWidth={2.0}
                                    baseSize={16}
                                    breakpoints={{ md: 16 }}
                                    className="mb-[1px] text-color-white-base"
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col w-full px-2 pt-3">
                        <div className="flex items-center">
                            <div className="flex-grow">
                                <div className="font-corp font-bold text-lg text-color-base-layout-logo tracking-widest line-clamp-2 leading-tight min-h-[2.5rem] flex">
                                    <TruncateText text={data.title} maxLength={50} breakpoints={{ md: 50 }} />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end items-center w-full font-cantarell font-bold text-[10px] text-color-base">
                            <IconAdaptive
                                Icon={Clock3}
                                strokeWidth={2.0}
                                baseSize={14}
                                breakpoints={{ md: 14 }}
                                className="mr-[2px]"
                            />
                            <TruncateText
                                text={formatDateStringWithDelimiter(data.published_date_time)}
                                maxLength={40}
                                breakpoints={{ md: 30 }}
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
            <ImageZoom
                isOpen={isZoomOpen}
                onClose={() => setIsZoomOpen(false)}
                src={data.eyecatch_public_url}
                alt={data.title}
            />
        </>
    );
}
